import * as React from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Power, PowerOff } from "lucide-react";
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { authClient } from '@/lib/auth-client';
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';

// Define the expected shape of the apiKey prop more explicitly if possible
// Assuming ApiKeyRow type is defined elsewhere or we define a minimal version here
interface ApiKeyRow {
  id: string;
  enabled: boolean;
  // Add other properties if needed by the component logic, though none seem required here
}

interface ToggleEnableApiKeyDialogProps {
  apiKey: ApiKeyRow;
  onToggled?: () => void; // Make onToggled optional
}

export const ToggleEnableApiKeyDialog: React.FC<ToggleEnableApiKeyDialogProps> = ({ apiKey, onToggled = () => {} }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = React.useState(false);
  const currentStatus = apiKey.enabled;
  const actionText = currentStatus ? t('settings.apiKeys.disableAction', 'Disable') : t('settings.apiKeys.enableAction', 'Enable');
  const confirmTitle = currentStatus ? t('settings.apiKeys.disableConfirmTitle', 'Disable API Key?') : t('settings.apiKeys.enableConfirmTitle', 'Enable API Key?');
  const confirmDescription = currentStatus
    ? t('settings.apiKeys.disableConfirmDescription', 'Disabling this key will prevent it from being used for authentication.')
    : t('settings.apiKeys.enableConfirmDescription', 'Enabling this key will allow it to be used for authentication.');
  const Icon = currentStatus ? PowerOff : Power;

  const toggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await authClient.apiKey.update({ keyId: apiKey.id, enabled });
      if (error) {
        throw new Error(error.message || t('settings.apiKeys.toggleErrorToast', 'Failed to update API Key status'));
      }
    },
    onSuccess: () => {
      const successMessage = currentStatus
        ? t('settings.apiKeys.disableSuccessToast', 'API Key disabled.')
        : t('settings.apiKeys.enableSuccessToast', 'API Key enabled.');
      toast.success(successMessage);
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      onToggled();
      setIsOpen(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setIsOpen(false);
    },
  });

  const handleToggleConfirm = () => {
    toggleMutation.mutate(!currentStatus);
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
          <Icon className="mr-2 h-4 w-4" />
          <span>{actionText}</span>
        </DropdownMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
          <AlertDialogDescription>{confirmDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleToggleConfirm}
            disabled={toggleMutation.isPending}
            className={currentStatus ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
          >
            {toggleMutation.isPending ? t('common.updating', 'Updating...') : actionText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};